import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = (import.meta.env.VITE_SITE_URL || 'https://yoybuy.cyou').replace(/\/$/, '');
const pagePath = '/yoybuy-spreadsheet-safety';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Безопасность покупок через YOYbuy Sheet: защита данных, платежей и как не стать жертвой мошенников',
  description: 'Насколько безопасно заказывать товары из Китая через yoybuy spreadsheet? Полный разбор защиты данных, безопасных платёжных методов, проверки продавцов и типичных схем обмана.',
  author: { '@type': 'Organization', name: 'YOYbuy Sheet' },
  publisher: {
    '@type': 'Organization',
    name: 'YOYbuy Sheet',
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg',
    },
  },
  datePublished: '2026-07-05',
  dateModified: '2026-07-05',
  url: `${siteUrl}${pagePath}`,
  image: 'https://readdy.ai/api/search-image?query=Minimalist%20conceptual%20photography%20of%20a%20modern%20laptop%20with%20a%20padlock%20icon%20on%20the%20screen%20placed%20on%20a%20clean%20white%20desk%20near%20a%20sunlit%20window%2C%20soft%20natural%20daylight%2C%20warm%20neutral%20cream%20background%2C%20symbolizing%20online%20security%20and%20data%20protection%2C%20editorial%20tech%20photography%20style%2C%20clean%20aesthetic%2C%20high%20detail%20on%20keyboard%20and%20screen&width=1400&height=700&seq=article-safety-hero-2026&orientation=landscape',
  inLanguage: 'ru',
  isAccessibleForFree: true,
};

export default function SafetyArticle() {
  return (
    <>
      <JsonLd data={articleSchema} />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <article className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-6 md:px-10">
            <div className="max-w-3xl mx-auto">
              <header className="mb-10">
                <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
                  <Link to="/" className="hover:text-primary-600 transition-colors">Главная</Link>
                  <span>/</span>
                  <span className="text-foreground-700">Безопасность</span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight">
                  Безопасность покупок через YOYbuy Sheet: защита данных, платежей и как не стать жертвой мошенников
                </h1>
                <p className="mt-4 text-sm md:text-base text-foreground-500">
                  Насколько безопасно заказывать товары из Китая через <strong>yoybuy spreadsheet</strong>? Полный разбор защиты данных, безопасных платёжных методов, проверки продавцов и типичных схем обмана.
                </p>
                <div className="mt-6 flex items-center gap-4 text-xs text-foreground-400">
                  <span>Обновлено: Июль 2026</span>
                  <span>·</span>
                  <span>Время чтения: 7 минут</span>
                </div>
              </header>

              <div className="mb-10 rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Minimalist%20conceptual%20photography%20of%20a%20modern%20laptop%20with%20a%20padlock%20icon%20on%20the%20screen%20placed%20on%20a%20clean%20white%20desk%20near%20a%20sunlit%20window%2C%20soft%20natural%20daylight%2C%20warm%20neutral%20cream%20background%2C%20symbolizing%20online%20security%20and%20data%20protection%2C%20editorial%20tech%20photography%20style%2C%20clean%20aesthetic%2C%20high%20detail%20on%20keyboard%20and%20screen&width=1400&height=700&seq=article-safety-hero-2026&orientation=landscape"
                  alt="Безопасность покупок через YOYbuy Sheet"
                  title="YOYbuy Sheet безопасность — защита при заказе из Китая"
                  className="w-full h-auto object-cover"
                />
              </div>

              <div className="prose-custom space-y-8 text-sm md:text-base text-foreground-700 leading-relaxed">
                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Насколько безопасно пользоваться YOYbuy Spreadsheet?
                  </h2>
                  <p>
                    Короткий ответ: абсолютно безопасно. <strong>Yoybuy spreadsheet</strong> — это просто инструмент для организации данных, электронная таблица, с которой вы работаете локально или в защищённом облаке. Она не передаёт ваши данные третьим лицам, не хранит платёжную информацию и не имеет доступа к вашим аккаунтам. Вы сами решаете, кому и когда отправлять заполненную таблицу.
                  </p>
                  <p>
                    Однако при заказе товаров из Китая безопасность складывается из нескольких факторов: надёжность продавца, защита платежа, правильное оформление заказа и прозрачность процесса доставки. <strong>Yoybuy sheet</strong> помогает вам контролировать все эти аспекты — рассмотрим каждый подробно.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Как YOYbuy Sheet защищает вас от ошибок и мошенников
                  </h2>
                  <div className="space-y-4">
                    {[
                      { name: 'Проверка продавца', desc: 'В yoybuy sheet есть поле для рейтинга продавца и количества отзывов. Заполняя эти данные, вы заставляете себя проверить репутацию магазина перед заказом — это отсекает 90% рисков.' },
                      { name: 'Фиксация цены', desc: 'Таблица фиксирует цену в юанях на момент заказа. Если продавец попытается изменить цену после оформления — у вас есть задокументированное подтверждение первоначальной стоимости.' },
                      { name: 'Контроль качества', desc: 'Чек-лист проверки в yoybuy spreadsheet обязывает агента осмотреть товар по вашим критериям и прислать фото до отправки. Без таблицы этот шаг часто пропускают.' },
                      { name: 'Прозрачность платежей', desc: 'Все комиссии, стоимость доставки и курсы конвертации прописаны в yoybuy sheet отдельными строками. Никаких скрытых платежей — любое отклонение от таблицы сразу видно.' },
                    ].map((item) => (
                      <div key={item.name} className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200">
                        <span className="w-1.5 h-1.5 mt-2 rounded-full bg-primary-500 flex-shrink-0"></span>
                        <div>
                          <span className="text-sm font-semibold text-foreground-900">{item.name}</span>
                          <p className="text-xs text-foreground-500 mt-1">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <div className="my-8 rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20desk%20setup%20with%20a%20notebook%20and%20pen%20next%20to%20a%20smartphone%20displaying%20a%20checklist%20interface%2C%20soft%20natural%20light%20from%20window%2C%20warm%20cream%20and%20beige%20color%20palette%2C%20organized%20workspace%20aesthetic%2C%20conceptual%20photography%20about%20verification%20and%20quality%20control%2C%20wooden%20desk%20texture%2C%20high%20detail%20on%20paper%20and%20screen&width=1200&height=600&seq=article-safety-checklist&orientation=landscape"
                    alt="YOYbuy Sheet — чек-лист безопасности при заказе из Китая"
                    title="Безопасность заказа через YOYbuy Spreadsheet"
                    className="w-full h-auto object-cover"
                  />
                </div>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Типичные риски при заказе из Китая и как YOYbuy Sheet помогает их избежать
                  </h2>
                  <div className="space-y-5">
                    <div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">Риск: товар не соответствует описанию</h3>
                      <p className="text-sm">Решение: в <strong>yoybuy sheet</strong> вы детально описываете каждый товар — цвет, размер, материал, особые приметы. Агент сверяет товар с описанием в таблице и присылает фото. Отклонение заметно сразу.</p>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">Риск: посылка потерялась</h3>
                      <p className="text-sm">Решение: в <strong>yoybuy spreadsheet</strong> фиксируется трек-номер и способ доставки. Вы всегда знаете, где ваша посылка. А при выборе страховки (отметка в таблице) — получаете компенсацию в случае потери.</p>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">Риск: неожиданные комиссии и наценки</h3>
                      <p className="text-sm">Решение: <strong>yoybuy sheet</strong> содержит прозрачный расчёт: цена товара + комиссия агента + доставка. Итоговая сумма известна до отправки заказа. Любое изменение требует вашего подтверждения.</p>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">Риск: неверная конвертация валют</h3>
                      <p className="text-sm">Решение: таблица YOYbuy фиксирует курс юаня на момент заказа. Вы платите ровно столько, сколько рассчитали, без «плавающих» курсовых наценок.</p>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Заключение
                  </h2>
                  <p>
                    Безопасность заказов из Китая — это не магия, а системный подход. <strong>Yoybuy spreadsheet</strong> даёт вам именно такой подход: структурированный, прозрачный и контролируемый на каждом этапе. Вернитесь на <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">главную страницу</Link> и убедитесь сами. Читайте также: <Link to="/yoybuy-spreadsheet-vs-agents" className="text-primary-600 hover:text-primary-700 underline font-medium">таблицы против агентов</Link> и <Link to="/yoybuy-sheet-guide" className="text-primary-600 hover:text-primary-700 underline font-medium">полное руководство</Link>.
                  </p>
                </section>
              </div>

              <div className="mt-12 pt-8 border-t border-background-300">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-foreground-500">Было полезно? Поделитесь:</p>
                    <div className="flex items-center gap-3 mt-2">
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-twitter-x-line"></i>
                      </a>
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-telegram-line"></i>
                      </a>
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-facebook-line"></i>
                      </a>
                    </div>
                  </div>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Вернуться на главную
                  </Link>
                </div>
              </div>

              <div className="mt-10 p-6 rounded-xl bg-background-100 border border-background-200">
                <p className="text-sm font-semibold text-foreground-900 mb-2">Читайте также:</p>
                <div className="flex flex-wrap gap-2">
                  <Link to="/yoybuy-sheet-guide" className="text-xs text-primary-600 hover:text-primary-700 underline">Руководство по YOYbuy Sheet</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-vs-agents" className="text-xs text-primary-600 hover:text-primary-700 underline">Таблицы vs Агенты</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-shipping" className="text-xs text-primary-600 hover:text-primary-700 underline">Доставка из Китая</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/" className="text-xs text-primary-600 hover:text-primary-700 underline">Главная</Link>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}